/**
 * ADR-0223 — a recurring meeting's own page (`/app/collab/series/:id`; the
 * owner, 2026-11-04: "when I click on a recurring meeting, I'd expect to see
 * all previous sessions and then be able to go in and add a new session"):
 * the meeting, its next occurrence and agenda, every recorded session newest
 * first, and the past occurrences nobody recorded — each a place to record.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { CollabPage, errMessage, Face, type SeriesPage } from "../components/collab.js";

export function CollabSeries() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState<SeriesPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setPage(null);
    void apiFetch<SeriesPage>(`/api/tenant/collab/series/${encodeURIComponent(id)}/sessions`)
      .then((r) => {
        if (!cancelled) setPage(r);
      })
      .catch((e) => {
        if (!cancelled) setError(errMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);
  const crumb = (
    <span className="collabCrumb">
      <Link to="/app/collab/meetings">Meetings</Link> / {page?.series.name ?? "…"}
    </span>
  );
  return (
    <CollabPage title={page?.series.name ?? "Meeting"} crumb={crumb}>
      {error ? <ErrorState detail={error} /> : null}
      {!error && !page ? <LoadingState /> : null}
      {page ? <SeriesView page={page} /> : null}
    </CollabPage>
  );
}

/** Pure, for the tests. */
export function SeriesView(props: { page: SeriesPage }) {
  const { series, sessions, missed, can } = props.page;
  return (
    <article className="collabSheet">
      <div className="collabSheetMain">
        <div className="collabMasthead" style={{ gridTemplateColumns: "minmax(0, 1fr)" }}>
          <div>
            <div className="collabMastTitle">{series.name}</div>
            <div className="collabMastMeta">
              <span>{series.when_line}</span>
              {series.duration_minutes ? <span>{series.duration_minutes} min</span> : null}
              {series.members.length ? (
                <>
                  <span className="collabFaces">
                    {series.members.map((m) => (
                      <Face key={m.account_id} face={{ name: m.name, initials: initialsOf(m.name) }} />
                    ))}
                  </span>
                  <span>{series.members.length} members</span>
                </>
              ) : null}
              {can.edit ? (
                <Link className="collabLinkBtn" to={`/app/collab/series/${series.id}/edit`}>
                  Edit meeting
                </Link>
              ) : null}
            </div>
            {series.purpose ? <p className="collabSideText" style={{ marginTop: 10, maxWidth: "72ch" }}>{series.purpose}</p> : null}
          </div>
        </div>
        {series.next ? (
          <Link className="collabCarry" to={series.next.href}>
            <span>
              <b>Next</b> · {series.next.label.replace(/^Next · /, "")} {series.next.day_number} · {series.next.line}
            </span>
            <span className="go">See the agenda →</span>
          </Link>
        ) : (
          <div className="collabEmpty">This meeting has ended.</div>
        )}
        <section className="collabPart">
          <div className="collabPartHead">
            <h2 className="collabH2">Sessions</h2>
            <span>{sessions.length}</span>
          </div>
          {sessions.length === 0 ? <div className="collabEmpty">No session recorded yet.</div> : null}
          <ol className="collabRaised">
            {sessions.map((s) => (
              <li key={s.id}>
                <div>
                  <Link className="collabRTitle" to={s.href}>
                    {s.date_label}
                  </Link>
                  <div className="collabWent">
                    <span>{s.line}</span>
                    {s.learned ? <span>· {s.learned} learned</span> : null}
                    {s.noted ? <span>· {s.noted} noted</span> : null}
                    {s.attended ? <span>· {s.attended} attended</span> : null}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
        {can.record && missed.length ? (
          <section className="collabPart">
            <div className="collabPartHead">
              <h2 className="collabH2">Not recorded</h2>
              <span>past meetings with no minutes — open one to record it</span>
            </div>
            <ul className="collabNoted">
              {missed.map((m) => (
                <li key={m.date}>
                  <Link to={m.href}>{m.label}</Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
      <aside className="collabSheetSide">
        <div>
          <div className="collabSideHead">About this meeting</div>
          <div className="collabSideText">
            {series.purpose ? (
              <>
                {series.purpose}
                <br />
                <br />
              </>
            ) : null}
            {series.meets_line}
          </div>
        </div>
        {series.last ? (
          <div>
            <div className="collabSideHead">Last session</div>
            <div className="collabSideText">
              <Link to={`/app/collab/sessions/${series.last.session_id}`}>{series.last.line}</Link>
            </div>
          </div>
        ) : null}
      </aside>
    </article>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length <= 1 ? (parts[0] ?? "?").slice(0, 2).toUpperCase() : `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}
